/**
 * WHERE THE CALL IS, AND WHERE "NOT ON THE CALL" IS — the two routes a live
 * 1:1 moves between, written once.
 *
 * The call stage is not a route of its own: it is a fixed overlay rendered by
 * `GiverFlow`/`SeekerFlow` from inside the empathy page. So "go back to the
 * call" and "leave the stage" are both ordinary navigations, and the two
 * addresses have to agree across the pill's "Return to call", the dashboard's
 * leave-queue guard and the Back handler. They were three string literals; a
 * fourth caller getting the `?tab=1on1` wrong lands on the circles tab with a
 * call running and nothing on screen to return to (class D).
 */

/** The 1:1 tab of the empathy page — where the stage mounts. */
export const CALL_ROUTE = '/app/empathy?tab=1on1';

/**
 * Where Back goes from the stage. A known page rather than `navigate(-1)`:
 * the flow auto-navigates to the empathy page when a match lands, so history
 * behind a call is as likely to be the empathy page again as anywhere, and
 * "Back appears to do nothing" is the other half of the bug that had Back
 * hanging up. The dashboard home always has the pill on it.
 */
export const OFF_STAGE_ROUTE = '/app';

/**
 * Where a 1:1 sub-page's own Back control goes (R2, David 2026-10-19: "if I
 * hit back I get sent to the circles"). The page that linked here may hand
 * over where it is as `state.backTo` (the group's Listeners panel does, so
 * Back returns to that group's Empathy tab, and so does the booked-call page
 * a booking made there opens); anything else, or a value that is not a plain
 * in-app path, is the 1:1 tab. Never bare `/app/empathy`: that is
 * the Circles tab.
 */
export function subPageBackTarget(state: unknown): string {
  const backTo = (state as { backTo?: unknown } | null)?.backTo;
  return typeof backTo === 'string' && isPlainAppPath(backTo) ? backTo : CALL_ROUTE;
}

/**
 * `/app`, then non-empty segments of URL-safe characters (no `.`/`..` walk),
 * then an optional query: no empty segment a browser could read as a host
 * (`/app//evil.example`), no backslash, no fragment (review #999 item 2).
 */
const PLAIN_APP_PATH = /^\/app(?:\/[A-Za-z0-9_~%-][A-Za-z0-9._~%-]*)*(?:\?[^#\\\s]*)?$/;

function isPlainAppPath(path: string): boolean {
  return PLAIN_APP_PATH.test(path);
}

/** The `state` a link into a 1:1 sub-page passes so its Back returns to `here`. */
export function backToHere(here: { pathname: string; search: string }): { backTo: string } {
  return { backTo: `${here.pathname}${here.search}` };
}
